import { Box, LinearProgress, useTheme } from "@mui/material"
import { useStore } from "@nanostores/react"
import { useEffect, useRef } from "react"

import type {
  ResolutionString,
  ChartingLibraryWidgetOptions,
  IBasicDataFeed,
} from "../charting_library/charting_library"

import { widget } from "../charting_library/charting_library.esm"
import { $chartLoading } from "@/utils/tradingview-utils"

const containerId = "tradingview_chart"

type TradingViewChartProps = {
  symbol: string
  df: IBasicDataFeed
}

export function TradingViewChart(props: TradingViewChartProps) {
  const { symbol, df } = props

  const widgetRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>
  const theme = useTheme()

  useEffect(() => {
    const options: ChartingLibraryWidgetOptions = {
      autosize: true,
      container: widgetRef.current,
      datafeed: df,
      interval: "15" as ResolutionString,
      library_path: "./charting_library/",
      locale: "en",
      symbol,
      theme: "dark",
      // debug: true,
      custom_css_url: "/charting_library.css",
      disabled_features: [
        "header_symbol_search",
        "header_quick_search",
        "symbol_search_hot_key",
        "header_compare",
      ],
      enabled_features: ["save_chart_properties_to_local_storage", "use_localstorage_for_settings"],
      favorites: {
        intervals: ["1", "15", "60", "1D"] as ResolutionString[],
        chartTypes: ["Line", "Area", "Candles"],
      },
      loading_screen: {
        backgroundColor: theme.palette.background.paper,
        foregroundColor: theme.palette.accent.main,
      },
    }

    const tvWidget = new widget(options)

    tvWidget.onChartReady(() => {
      tvWidget.applyOverrides({
        "paneProperties.background": theme.palette.background.paper,
        "paneProperties.backgroundType": "solid",
      })
    })

    return () => {
      tvWidget.remove()
    }
  }, [symbol, df, theme, widgetRef])

  return (
    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
      <Box sx={{ height: "100%", width: "100%" }} ref={widgetRef} id={containerId} />
      <ChartLoading />
    </Box>
  )
}

const ChartLoading = () => {
  const chartLoading = useStore($chartLoading)

  if (!chartLoading) return null

  return (
    <LinearProgress
      color="accent"
      variant="indeterminate"
      sx={{ height: 4, position: "absolute", top: 38, zIndex: 1000, width: "100%" }}
    />
  )
}
